import React, { FC } from "react";
import { BADGE_CATEGORY, BadgeDefinition, BadgeLadderTier } from "@shared/models";

// Same-directory now that CoreValueCard lives here too (PR2 relocation, consolidating the two
// core-value card rows into one) — still a relative import, not the `@containers` alias every OTHER
// cross-container reference in this repo uses: that alias has no jest moduleNameMapper entry (only
// elided TYPE-ONLY alias imports resolve under jest, via babel stripping them before module
// resolution runs), and this component genuinely needs the real CoreValueCard to render under test —
// the pairing test below asserts real DOM output (title, tier-label text, pip classes), not a
// mocked stand-in.
import { CoreValueCard, LadderPipState } from "./CoreValueCard";
import { ladderTierLabel } from "../ladderFillPercent";
// Relative, not aliased: jest resolves no path aliases here, and this component is rendered by
// MySparksContainer's own suite, which would otherwise have to virtual-mock CardHead to do it.
import { CardHead } from "../../CardHead";

import "./index.scss";

// Fallback rung count when the badge catalog fetch has failed or not yet completed
// (`badgeDefinitions: null` — the two fetches this container makes fail independently, see
// `SparksState.interface.tsx`'s `badgeDefinitionsLoadFailed`). Matches the Phase-20 seed's per-value
// ladder length (overview-rebuild-pr2-plan.md, "How many pips?"). As long as no value's real catalog
// ever grows past 8 tiers, this can only make the derived count too LARGE, never too small — but
// that is a fact about today's seed data, not something this code enforces. If it ever stops holding,
// the clamp below still recovers the correct pip count from `currentTier.index`.
const FALLBACK_LADDER_TIER_COUNT = 8;

/**
 * How many rungs this core value's ladder has, derived from the badge catalog rather than
 * hardcoded (overview-rebuild-pr2-plan.md). Predicate: a VALUE-category definition, for this core
 * value, with a non-null tier. Deliberately no `is_active` filter — a ladder's length is structural
 * (catalog visibility and award eligibility are different questions), so hiding a rung from the
 * catalog must not shorten the ladder. `core_value_id` is nullable on `BadgeDefinition`; a strict
 * `===` naturally excludes a null core_value_id without needing a separate guard.
 *
 * @param badgeDefinitions - the full badge catalog, or `null` if it hasn't loaded / failed to load.
 * @param coreValueId - the core value to count rungs for.
 * @returns the number of catalog rungs for this value, or the fallback constant when the catalog is absent.
 */
const countCatalogRungs = (badgeDefinitions: BadgeDefinition[] | null, coreValueId: number): number =>
  badgeDefinitions
    ? badgeDefinitions.filter(
        (definition) =>
          definition.category === BADGE_CATEGORY.VALUE &&
          definition.core_value_id === coreValueId &&
          definition.tier !== null,
      ).length
    : FALLBACK_LADDER_TIER_COUNT;

/**
 * The pip count actually rendered, clamped so the current tier's pip is never dropped off the end
 * of a short or disagreeing catalog (overview-rebuild-pr2-plan.md's clamp). The catalog is a
 * separate fetch from the server-computed current tier; any disagreement between them — a deleted
 * rung, a partial catalog, a stale cache — would otherwise make the pip rule below match no pip at
 * all, silently rendering an all-earned track with no current marker.
 *
 * @param derivedRungCount - the catalog-derived rung count from `countCatalogRungs`.
 * @param currentTier - the tier already reached, or `null` if not yet in tier 1.
 * @returns the number of pips to render, always at least enough to include the current tier's index.
 */
const clampPipCount = (derivedRungCount: number, currentTier: BadgeLadderTier | null): number =>
  Math.max(derivedRungCount, currentTier ? currentTier.index + 1 : 0);

/**
 * Classes each pip position 0..n-1 as earned, current, or locked around the current tier's
 * **0-based** index (`badge_definitions.tier` is 1-based; `current_tier.index` is not — see
 * `badge.service.ts`, cited in overview-rebuild-pr2-plan.md). `null` current tier locks every pip.
 *
 * @param pipCount - the clamped number of pips to render.
 * @param currentTier - the tier already reached, or `null` if not yet in tier 1.
 * @returns one `LadderPipState` per pip position, in order.
 */
/**
 * The heading's right-hand caption. The frame (9119:950) reads "· eight tiers each", but this component
 * derives each ladder's length per value, so hardcoding "eight" would let the heading assert something the
 * rows below it contradict — exactly the assumption this PR exists to remove. So the caption states the
 * count only when every ladder genuinely has the same one, and says nothing otherwise rather than picking
 * a number to print.
 *
 * @param pipCounts - the clamped pip count actually rendered for each ladder, in row order.
 * @returns the caption text, or `null` when the ladders differ in length.
 */
const ladderCountCaption = (pipCounts: number[]): string | null => {
  const [first] = pipCounts;
  if (first === undefined) return null;
  return pipCounts.every((count) => count === first) ? `· ${first} tiers each` : null;
};

const buildPipStates = (pipCount: number, currentTier: BadgeLadderTier | null): LadderPipState[] =>
  Array.from({ length: pipCount }, (_unused, position) => {
    if (!currentTier) return "locked";
    if (position < currentTier.index) return "earned";
    if (position === currentTier.index) return "current";
    return "locked";
  });

/**
 * The joined view-model this component renders. Built once in `MySparksContainer` (D2 of the
 * phase-35 plan) by matching `PerValueProgress` (badges endpoint — has tier info, no title/icon) with
 * `UserCoreValuesSummary` (sparks-summary endpoint — has title/icon, no tier info) on `core_value_id`.
 * `ValueLadderCards` never joins or fetches anything itself — it only renders what it's given.
 */
export interface ValueLadderCardData {
  coreValueId: number;
  title: string;
  iconUrl: string;
  totalScore: number;
  currentTier: BadgeLadderTier | null;
  nextTier: BadgeLadderTier | null;
  pointsToNext: number | null;
}

interface ValueLadderCardsProps {
  items: ValueLadderCardData[];
  // The badge catalog (`GET /badge-definitions`), used only to derive each ladder's rung count —
  // `null` while it hasn't loaded yet or failed to load, independently of `items` itself
  // (overview-rebuild-pr2-plan.md, "How many pips?").
  badgeDefinitions: BadgeDefinition[] | null;
  // The clicked-and-currently-filtered-on core value, if any — read from `MySparksContainer`
  // (`receivedRecognitionsFilter?.core_value_id`) and passed down as a plain prop so this component
  // stays presentational (see the click-to-filter note below).
  selectedCoreValueId?: number;
  // PR2: the click-to-filter handler, ported to `MySparksContainer` (which owns the redux dispatch
  // and the activeTab/URL sync) and forwarded here unchanged — this component never dispatches or
  // selects on its own.
  onClickHandler: (coreValueId: number) => void;
}

/**
 * A row of `CoreValueCard`s on the My Sparks tab — one per core value, each a horizontal tier-track
 * row (overview-rebuild-pr2-plan.md, design node 9119:950). Consolidated with the old, separate
 * click-to-filter row (`RecognitionListCoreValuesSummary`, now deleted): clicking a row here toggles
 * the received-recognitions filter via `onClickHandler`, and `isSelected` is computed per row from
 * `selectedCoreValueId` — both are plain props from `MySparksContainer`, so this component stays
 * presentational: the join, the tier data, the catalog, the click handler, and the selection state
 * all come from props. `ladderFillPercent` is deliberately NOT used here — pips are discrete, drawn
 * from the current tier's index, not a continuous fill percentage (that helper stays for
 * `FlameProgress`'s bar and `buildBadgeCards`, untouched by this change).
 */
const ValueLadderCards: FC<ValueLadderCardsProps> = (props) => {
  const { items, badgeDefinitions, selectedCoreValueId, onClickHandler } = props;

  const pipCountsByRow = items.map(({ coreValueId, currentTier }) =>
    clampPipCount(countCatalogRungs(badgeDefinitions, coreValueId), currentTier),
  );
  const countCaption = ladderCountCaption(pipCountsByRow);

  return (
    <div className="value-ladder-cards">
      {/* The shared SPARK/CardHead in its `caption` variant -- arrow OFF, label ON. "· eight tiers
          each" is the example Figma's own component description gives for a caption, and the rule it
          states is that an arrow means the head navigates: this head does not, so it has none. The
          label is still derived rather than hardcoded (see ladderCountCaption above). */}
      <CardHead
        title="Your five SPARK ladders"
        slot={countCaption ? { kind: "caption", label: countCaption } : undefined}
        className="value-ladder-cards-heading"
      />
      {items.map((item, index) => {
        const { coreValueId, title, iconUrl, totalScore, currentTier } = item;
        const tierLabel = ladderTierLabel(currentTier, title);
        const pipCount = pipCountsByRow[index];
        const pipStates = buildPipStates(pipCount, currentTier);

        return (
          <div key={coreValueId} data-testid={`ladder-card-${coreValueId}`}>
            <CoreValueCard
              valueTitle={title}
              totalScore={totalScore}
              coreValueId={coreValueId}
              onClickHandler={onClickHandler}
              isSelected={coreValueId === selectedCoreValueId}
              iconUrl={iconUrl}
              tierLabel={tierLabel}
              pipStates={pipStates}
              currentTierThreshold={currentTier?.threshold ?? null}
            />
          </div>
        );
      })}
    </div>
  );
};

export default ValueLadderCards;
